import axios from "axios";
import { getStorage, clearStorage } from "../../utils/storage";
import { APP_CONFIG } from "./apiConfig";
import { methodType } from "./apiEndPoints";

const DEFAULT_ERROR_MESSAGE = "An unexpected error occurred";

/** @returns {string[]} */
const flattenErrorValue = (value) => {
  if (value == null) return [];
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(flattenErrorValue);
  if (typeof value === "object") {
    return Object.values(value).flatMap(flattenErrorValue);
  }
  return [String(value)];
};

/**
 * Builds a display string from API `error` payloads (field map or bare envelope).
 * Prefers `non_field_errors`, then other field messages, then envelope `message`.
 */
export const formatApiErrorMessage = (body, fallback = DEFAULT_ERROR_MESSAGE) => {
  if (body == null) return fallback;
  if (typeof body === "string") return body;

  const errorBlock =
    body?.error && typeof body.error === "object" && !Array.isArray(body.error)
      ? body.error
      : typeof body === "object" &&
        !body.success &&
        !body.results &&
        (body.non_field_errors != null || Object.keys(body).some((k) => k !== "message" && k !== "status"))
        ? body
        : null;

  if (errorBlock && typeof errorBlock === "object") {
    const nonField = flattenErrorValue(errorBlock.non_field_errors);
    if (nonField.length) return nonField.join(" ");

    const fieldMessages = Object.entries(errorBlock)
      .filter(([key]) => key !== "non_field_errors")
      .flatMap(([, value]) => flattenErrorValue(value));

    if (fieldMessages.length) return fieldMessages.join(" ");
  }

  if (typeof body?.message === "string" && body.message.trim()) {
    return body.message;
  }

  return fallback;
};

/**
 * Normalizes per-field API errors to string or string[] values.
 * @returns {Record<string, string | string[]>}
 */
export const normalizeApiErrors = (errorBlock) => {
  if (!errorBlock || typeof errorBlock !== "object" || Array.isArray(errorBlock)) {
    return {};
  }

  return Object.entries(errorBlock).reduce((acc, [key, value]) => {
    const parts = flattenErrorValue(value);
    if (!parts.length) return acc;
    acc[key] = parts.length === 1 ? parts[0] : parts;
    return acc;
  }, {});
};

/**
 * Normalizes V2 API error envelopes (HTTP errors or `success: false` bodies).
 */
export const parseApiErrorResponse = (body, httpStatus) => {
  const envelope =
    body && typeof body === "object" && (body.error != null || body.success === false)
      ? body
      : body?.error
        ? { success: false, error: body.error, message: body.message, status: body.status }
        : { success: false, error: body };

  const status = envelope.status ?? httpStatus ?? envelope.error?.status;
  const errorBlock =
    envelope.error && typeof envelope.error === "object" && !Array.isArray(envelope.error)
      ? envelope.error
      : {};

  const message = formatApiErrorMessage(envelope, envelope.message || DEFAULT_ERROR_MESSAGE);

  return {
    success: false,
    message,
    status,
    error: errorBlock,
    errors: normalizeApiErrors(errorBlock),
  };
};

const apiClient = axios.create();

apiClient.interceptors.response.use(
  (response) => {
    if (!response?.data) {
      return Promise.reject(new Error("Invalid response object"));
    }
    const { data } = response;
    if (response.config?.responseType === "blob") {
      return data;
    }
    return data;
  },
  async (error) => {
    if (!error) {
      return Promise.reject(new Error("Unknown error occurred"));
    }

    if (!error.response) {
      return Promise.reject(error);
    }

    const { data, config, status } = error.response;

    if (status === 401 && config?.url && !config.url.includes("login")) {
      clearStorage();
    }

    if (data) {
      return Promise.reject(data);
    }

    return Promise.reject({
      success: false,
      message: error.message || "An error occurred",
      status,
    });
  }
);

export const performRequestV2 = async (
  method,
  url,
  data,
  login = true,
  formData = false,
  extraConfig = {}
) => {
  url = url.replaceAll("#", "%23");
  const config = {
    method,
    url,
    baseURL: APP_CONFIG.API_BASE_URL,
    timeout: 30000,
  };

  if (
    method === methodType.PUT ||
    method === methodType.PATCH ||
    method === methodType.POST ||
    method === methodType.DELETE
  ) {
    config.data = data;
  }

  if (method === methodType.GET) {
    config.params = data;
  }

  config.headers = formData
    ? { "Content-Type": "multipart/form-data" }
    : { "Content-Type": "application/json; charset=utf-8" };

  if (login) {
    const token = getStorage("access_token") || null;
    config.headers.Authorization = `Bearer ${token}`;
  }

  return apiClient.request({
    ...config,
    ...extraConfig,
  });
};

/**
 * V2 API wrapper — parses envelope errors into `message` + `errors` for UI display.
 * @returns {Promise<any>} API body on success; `{ success: false, message, error, errors, status }` on failure
 */
export const apiRequestV2 = async (
  method,
  endpoint,
  data = {},
  requiresAuth = true,
  isFormData = false
) => {
  try {
    const cleanEndpoint = endpoint.startsWith(APP_CONFIG.API_BASE_URL)
      ? endpoint.replace(APP_CONFIG.API_BASE_URL, "")
      : endpoint;

    const response = await performRequestV2(
      method,
      cleanEndpoint,
      data,
      requiresAuth,
      isFormData
    );

    if (response?.success === false) {
      return parseApiErrorResponse(response);
    }

    return response;
  } catch (error) {
    const httpStatus = error?.response?.status ?? error?.status;
    const body = error?.response?.data ?? error;

    if (body && typeof body === "object" && (body.error != null || body.success === false)) {
      return parseApiErrorResponse(body, httpStatus);
    }

    return parseApiErrorResponse(
      {
        success: false,
        message: error?.message || DEFAULT_ERROR_MESSAGE,
        status: httpStatus,
        error: {},
      },
      httpStatus
    );
  }
};
